import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);
const TOKEN_KEY = 'mm_token';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setLoading(false); return; }

    // Only a 401 means the token is genuinely invalid/expired — that's the
    // one case worth logging the user out for. Anything else (429 rate
    // limit, a transient 5xx, a network blip) says nothing about whether
    // the token is valid, so clearing it would silently sign out a user
    // with a perfectly good session just because one request had a hiccup.
    // Retry those a few times with backoff before giving up.
    let cancelled = false;
    const verify = async (attempt = 0) => {
      try {
        const res = await api.get('/auth/me', { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setUser(res.data.user);
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
        } else if (attempt < 3) {
          await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
          if (!cancelled) return verify(attempt + 1);
        }
        // Non-401 failure after retries: leave the token in place. The user
        // will see the login screen this one time, but their session isn't
        // destroyed — reloading once the server recovers will pick it back up.
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    verify();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const res = await api.post('/auth/login', { email, password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setUser(res.data.user);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || 'Unable to sign in' };
    }
  }, []);

  const signup = useCallback(async (email, password, name) => {
    try {
      const res = await api.post('/auth/signup', { email, password, name });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setUser(res.data.user);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || 'Unable to create account' };
    }
  }, []);

  // credential is the ID token JWT handed back by Google Identity Services
  // after the user picks an account in the Google button/prompt.
  const loginWithGoogle = useCallback(async (credential) => {
    try {
      const res = await api.post('/auth/google', { credential });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setUser(res.data.user);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || 'Google sign-in failed' };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, signup, loginWithGoogle, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
